/**
 * Customer 360 payload types — matches the backend response shape, which
 * is the raw JSON served by the customer-profile data provider.
 *
 * See docs/customer360/example{1,2,3,3.full}.json for reference payloads.
 * The current backend demo serves the "full" shape (example3.full), which
 * has raw Connect Cases field arrays, richer connected-device/cook signals,
 * 200-field shipment rows, and source `raw_profiles` pre-merge.
 *
 * Fields that vary across source systems are optional. snake_case throughout.
 */

export interface Customer360Response {
  profiles_found: boolean;
  profile_count: number;
  profiles: CustomerProfile[];
}

export interface CustomerProfile {
  /** Comma-separated list of source profile IDs that were merged. */
  profile_id: string;
  first_name: string | null;
  last_name: string | null;
  emails: string[];
  phones: string[];
  account_number?: string | null;
  attributes?: Record<string, string>;
  /** Present in the full shape — source profiles before merge. */
  raw_profiles?: RawProfile[];
  cases: CaseItem[];
  assets: AssetItem[];
  asset_enrichments?: AssetItem[];
  sf_cases: unknown[];
  shipments: ShipmentItem[];
  connected_devices: ConnectedDevice[];
  cooks: CookItem[];
}

// ---------------------------------------------------------------------------
// Raw pre-merge profile (from the "full" shape)
// ---------------------------------------------------------------------------

export interface RawProfileAddress {
  Address1?: string;
  Address2?: string;
  Address3?: string;
  City?: string;
  State?: string;
  Country?: string;
  PostalCode?: string;
}

export interface RawProfile {
  ProfileId: string;
  PartyType?: string;
  BusinessName?: string;
  FirstName?: string;
  LastName?: string;
  PhoneNumber?: string;
  EmailAddress?: string;
  PersonalEmailAddress?: string;
  Address?: RawProfileAddress;
  ShippingAddress?: RawProfileAddress;
  MailingAddress?: RawProfileAddress;
  BillingAddress?: RawProfileAddress;
  Attributes?: {
    sfdcAccountId?: string;
    sfdcContactId?: string;
    [key: string]: string | undefined;
  };
  FoundByItems?: Array<{ KeyName: string; Values: string[] }>;
}

// ---------------------------------------------------------------------------
// Cases — raw Connect Cases shape (fields as [{id, value: {stringValue}}])
// ---------------------------------------------------------------------------

export interface CaseField {
  id: string;
  value: {
    stringValue?: string;
    booleanValue?: boolean;
    doubleValue?: number;
    [key: string]: unknown;
  };
}

export interface CaseItem {
  /** Back-compat id for the simplified shape. */
  case_id?: string;
  /** Raw Connect caseId (present in "full" shape). */
  caseId?: string;
  templateId?: string;
  fields?: CaseField[];
  tags?: Record<string, unknown>;

  // Simplified-shape fields (absent in "full" shape; the viewmodel extracts
  // equivalents from `fields[]`).
  title?: string;
  status?: string;
  reference_number?: string;
  created_datetime?: string;
  last_updated_datetime?: string;
  last_closed_datetime?: string;
  summary?: string;
  brand?: string;
  asset?: string;
  contact_id?: string;
  loupe_link?: string;
  // Connect-resolved agent attribution; populated server-side for both
  // /short and /full payloads (the /full path injects them as flat
  // properties on each case after looking them up via Connect).
  agent_name?: string;
  bpo?: string;
  site?: string;
}

// ---------------------------------------------------------------------------
// Assets — both legacy simplified and the richer "asset_enrichments" shape
// ---------------------------------------------------------------------------

export interface AssetItem {
  // Simplified shape
  name?: string;
  product_code?: string;
  product_family?: string;
  product_description?: string;
  install_date?: string | null;
  created_date?: string;
  category?: string | { l1?: string; l2?: string; series?: string; model?: string };
  controller_type?: string | null;
  grill_line?: string | null;
  model_family?: string | null;
  firmware_version?: string | null;
  last_connected?: string | null;
  wifi_name?: string | null;
  wifi_signal_strength?: number | null;

  // Enriched shape (from asset_enrichments / full assets)
  serial_number?: string;
  uuid?: string;
  asset_name?: string;
  product_sku?: string;
  is_courtesy_item?: boolean | null;
  product_category_level_1?: string | null;
  product_category_level_2?: string | null;
  product_category_level_3?: string | null;
  product_category_level_4?: string | null;
  controller_family?: string | null;
  grill_family?: string | null;
  device_trade_name?: string | null;
  device_uuid?: string | null;
  config_version?: string | null;
  last_connected_date?: string | null;
  device_friendly_name?: string | null;
  /** Email of the IoT-account owner that registered the connected device.
   *  Surfaced by the Redshift enrichment row on each asset; the viewmodel
   *  uses it to detect device-vs-asset owner mismatches after a transfer. */
  device_owner_email?: string | null;

  status?: string;

  // Raw Salesforce Customer-Profiles asset shape (PascalCase). The
  // calltree-tools-ui /full endpoint passes Connect Profiles assets through
  // un-flattened, so the viewmodel falls back to these when the snake_case
  // aliases are missing. The agent app's payload does the snake_case
  // pre-flattening server-side, so these stay undefined there.
  Name?: string;
  SerialNumber?: string;
  ProductCode?: string;
  ProductFamily?: string;
  ProductDescription?: string;
  Status?: string;
  InstallDate?: string | null;
  CreatedDate?: string;
  Uuid?: string | null;

  // Raw Salesforce lowercase shape (c360_search Lambda pass-through).
  // Salesforce API returns these as all-lowercase when queried via SOQL.
  serialnumber?: string;
  createddate?: string;
  purchasedate?: string | null;
  product2_code?: string;
  product2_name?: string;
  product_name__c?: string;
  uuid__c?: string | null;
  product_group?: string;
  msrp?: number | null;
}

// ---------------------------------------------------------------------------
// Shipments — 200-field "full" shape (only the agent-relevant subset is typed)
// ---------------------------------------------------------------------------

export interface ShipmentItem {
  order_number: number;
  order_date: string;
  order_line_number?: number;
  product_description: string;
  product_sku: string;

  // Shipping status
  ship_status?: string | null;
  ship_date?: string | null;
  shipped_datetime?: string | null;
  is_delivered?: boolean | null;
  is_delivered_on_time?: boolean | null;
  delivered_date?: string | null;
  delivered_datetime?: string | null;
  original_delivery_date?: string | null;
  days_late?: number | null;
  number_of_appointment_changes?: number | null;
  is_appointment_rescheduled?: boolean | null;

  // Tracking
  tracking_number?: string | null;
  ship_header_tracking_number?: string | null;
  consolidated_tracking_number?: string | null;
  shipper_name?: string | null;
  /** Nicer, human-readable carrier label (from "full" shape). */
  carrier_name?: string | null;
  uf_shipper_name?: string | null;

  // Destination
  destination_address_1?: string | null;
  destination_city?: string | null;
  destination_state_province?: string | null;
  destination_country?: string | null;
  destination_postal_code?: string | null;
  ship_to_email?: string | null;
  ship_to_phone_number?: string | null;
  is_residential_delivery?: boolean | null;
  is_drop_ship?: boolean | null;

  // Warehouse / carrier operations
  warehouse_description?: string | null;
  warehouse_name?: string | null;
  plant_name?: string | null;

  // Money / qty
  extended_price_usd?: string | number | null;
  requested_quantity?: string | number | null;
  shipped_quantity?: string | number | null;
  order_quantity?: string | number | null;
  payment_terms_description?: string | null;
  customer_po_number?: string | null;

  // Source
  source_system?: string | null;
  shipment_type?: string | null;
  mode_type?: string | null;
}

// ---------------------------------------------------------------------------
// Connected devices — richer "full" shape
// ---------------------------------------------------------------------------

export interface ConnectedDevice {
  // "full" shape
  device_uuid?: string;
  device_type_id?: number;
  trade_name?: string;
  config_version?: string | null;
  last_connected_date?: string | null;
  device_latitude?: number | null;
  device_longitude?: number | null;
  device_time_zone_name?: string | null;
  device_friendly_name?: string | null;
  device_owner_email?: string | null;
  owner_full_name?: string | null;
  manufactured_at?: string | null;
  fleet_entry_date?: string | null;
  reported_skill_level?: string | null;
  google_maps_link?: string | null;
  sfcc_customer_id?: string | null;

  // Simplified shape
  controller_serial?: string;
  friendly_name?: string | null;
  firmware_version?: string | null;
  last_connected?: string | null;
}

// ---------------------------------------------------------------------------
// Cooks — richer "full" shape
// TODO: Traeger-specific (grills have cooks). Move to tenant model when
// model-driven views are implemented (Phase 2).
// ---------------------------------------------------------------------------

export interface CookItem {
  cook_id: string;
  device_uuid: string;

  // Timing
  cook_started_datetime?: string;
  started_at?: string;
  cook_start_date?: string;
  start_date?: string;
  first_event_at?: string;
  last_event_at?: string;
  total_cook_duration_seconds?: number | null;
  duration_seconds?: number | null;
  days_to_next_cook?: number | null;

  // Temps
  min_ambient_temp?: number | null;
  avg_ambient_temp?: number | null;
  max_ambient_temp?: number | null;
  min_goal_temp?: number | null;
  avg_goal_temp?: number | null;
  max_goal_temp?: number | null;
  min_probe_temp?: number | null;
  avg_probe_temp?: number | null;
  max_probe_temp?: number | null;

  // Alert counts
  critical_alert_count?: number | null;
  warning_alert_count?: number | null;
  info_alert_count?: number | null;
  grill_lid_opening_count?: number | null;
  number_of_setpoint_changes?: number | null;

  // Behavior flags
  has_frequent_setpoint_changes?: boolean | string | null;
  has_small_setpoint_changes?: boolean | string | null;
  problem_cook_type?: string | null;
  has_subsequent_problem_cook?: boolean | string | null;

  // Engagement
  got_it_count?: number | null;
  learn_more_count?: number | null;
  close_message_count?: number | null;
  image_count?: number | null;
  count_message_sent?: number | null;

  // Device summary (duplicated on each cook)
  device_running_total_cooks?: number | null;
  trade_name?: string | null;
  device_friendly_name?: string | null;
  device_time_zone_name?: string | null;

  // Legacy short aliases used in the simplified shape
  ambient_temp?: number | null;
  goal_temp?: number | null;
  probe_temp?: number | null;
  critical_alerts?: number | null;
  warning_alerts?: number | null;
  lid_openings?: number | null;
  setpoint_changes?: number | null;
  device_name?: string | null;
}

// ---------------------------------------------------------------------------
// Derived values
// ---------------------------------------------------------------------------

export function fullName(p: CustomerProfile): string {
  const joined = [p.first_name, p.last_name].filter(Boolean).join(' ').trim();
  return joined || p.attributes?.['Business Name'] || 'Unknown customer';
}

export function openCasesCount(p: CustomerProfile): number {
  return p.cases.filter((c) => getCaseField(c, 'status')?.toLowerCase() === 'open').length;
}

export function distinctOrdersCount(p: CustomerProfile): number {
  return new Set(p.shipments.map((s) => s.order_number)).size;
}

export function mergedProfileIds(p: CustomerProfile): string[] {
  return p.profile_id
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

/**
 * Connect Cases custom-field UUID → friendly name aliases. The /short
 * formatter on the server renames these UUIDs before returning the case;
 * the /full formatter passes them through raw, so the viewmodel needs the
 * same alias table client-side to find fields by friendly name.
 *
 * TODO: Traeger-specific UUIDs — every tenant has different Connect Cases
 * template field UUIDs. Move to tenant config or have the backend resolve
 * UUIDs before sending to the frontend (the /short formatter already does
 * this; the /full formatter should too).
 *
 * Keep this in sync with CASE_FIELD_LABELS in
 * calltree-tools-ui/src/lib/server/customer360.ts.
 */
const CASE_FIELD_ALIASES: Record<string, string> = {
  contact_id: 'b26e61c1-2d3a-42c5-9d49-a6210fa1967b',
  brand: 'da7af9ac-2a54-44f4-b5f0-e103ec57a36e',
  asset: 'aa5a3824-4e42-4529-8489-f18fa295afc5',
  item_sku: '4c4b16ee-8965-46fb-9c74-9cdc43031202',
  escalate_to_queue: 'ed03c82f-1f4c-4f20-a991-179967aab5fb',
  channel: '6a8b04f9-a961-412a-9468-286e5ad64493',
  case_record_type: '10cbe498-3a17-4422-98a7-b6cba689bde9',
  customer_facing_notes: '9994bc72-eaa9-40dd-b883-589189c0f0cd',
  internal_comments: '27e0dc33-6811-43ab-bd2b-faf6d78f2167',
  products_ordered: '7b429f46-318a-4a01-b438-777cf35a7dcc',
  service_order_number: '1258ebe3-9895-4ddb-a8eb-ed0a5478d063',
  order_number: 'a465eb03-2d9f-4bea-b9d7-ec5eaac1c40a',
  tracking_number: '23ddc82d-4b04-4a4f-9559-d98a9ceaabde',
  loupe_link: '98263457-cbb9-4b42-a3af-36f9a438f170',
};

/** Extract a stringValue from a raw Connect Case `fields[]` array by id,
 *  or return the flat property if already extracted. */
export function getCaseField(c: CaseItem, id: keyof CaseItem | string): string | undefined {
  // Flat property wins (simplified shape).
  const flat = (c as unknown as Record<string, unknown>)[id as string];
  if (typeof flat === 'string' && flat) return flat;
  // Fall back to raw fields[]. Try the friendly id first, then its
  // UUID alias if the backend hasn't pre-renamed it (the /full payload
  // doesn't, so without this aliasing fields like `loupe_link` would
  // never resolve in the agent-app layouts).
  if (!c.fields) return undefined;
  const aliasUuid = CASE_FIELD_ALIASES[id as string];
  const found = c.fields.find((f) => f.id === id || (aliasUuid && f.id === aliasUuid));
  return typeof found?.value?.stringValue === 'string' && found.value.stringValue
    ? found.value.stringValue
    : undefined;
}
